import {InfoTooltip} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import clsx from 'clsx';
import {isEqual} from 'lodash';
import React, {useCallback} from 'react';

import {useIsInLoadedReportContext} from '../../pages/ReportPage/LoadedReportContext';
import * as Run from '../../util/runs';
import {type Key} from '../../util/runTypes';
import * as RunTypes from '../../util/runTypes';
import {type WBTableRowWithRunSetInfo} from '../../util/tablecols';
import {runGroup as runGroupURL} from '../../util/urls';
import {TruncateTextTooltip} from '../common/TruncateText/TruncateTextTooltip';
import {type TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {useSingleRunLink} from '../MultiRunWorkspace/SingleRunMode/SingleRunLink';
import {isCrossProjectRun} from './util';

type LinkPathOpts = {
  disableRunLink: boolean;
  isGroup: boolean;
  isRunGroup: boolean;
  isSingleMode: boolean;
  rowName: string | null;
  rowProject?: {entityName: string; name: string};
  runsetProject: {entityName: string; name: string};
};

/**
 * Generates the URL for a run or run group in the run selector.
 * Returns undefined if the row should not be a link.
 *
 * For cross-project pinned runs, the link points to the run's source project
 * rather than the current workspace project.
 * @param opts {@link LinkPathOpts}
 * @returns URL for a run or run group, or undefined if no link should be generated.
 */
export function useLinkPath({
  disableRunLink,
  isGroup,
  isRunGroup,
  isSingleMode,
  rowName,
  rowProject,
  runsetProject,
}: LinkPathOpts): string | undefined {
  const entityName = rowProject?.entityName ?? runsetProject.entityName;
  const projectName = rowProject?.name ?? runsetProject.name;

  const runPath = useSingleRunLink({
    entityName,
    projectName,
    runName: rowName ?? '',
  });

  if ((isGroup && !isRunGroup) || disableRunLink || isSingleMode) {
    return undefined;
  }

  if (isRunGroup) {
    return runGroupURL({
      entityName,
      projectName,
      name: rowName,
      tab: 'workspace',
    });
  }

  return runPath;
}

type RowHeaderLabelProps = {
  /**
   * Custom name to show for this run in place of the project-level
   * run.displayName. Custom run names are scoped to `viewSpec.section`
   * (i.e. multi-run workspace or panel grid in a report)
   */
  customRunName?: string;
  /** If true, no link to the row/run will be generated for the label */
  disableRunLink: boolean;
  /** Entity name of the current runset (not necessarily the run's source project) */
  entityName: string;
  /**
   * The property this row has been grouped by, if any. This can be
   * undefined if the row is not grouped, which does not match the typedef.
   */
  groupedBy: Key;
  isHoveringRow: boolean;
  /** True if this row is the "container row" for a group of runs */
  isGroup: boolean;
  /**
   * True if this group row is a group and the group-by parameter
   * is the literal run group that the run belongs to. If this is true,
   * isGroup will always be true.
   */
  isRunGroup: boolean;
  /**
   * True when we want clicks to a run to _select_ that run instead of
   * navigating somewhere else. This is used in run selector tables
   * inside of the /group/ views.
   */
  isSingleMode: boolean;
  onSelectRunName: (name: string) => void;
  /** Project name of the current runset (not necessarily the run's source project) */
  projectName: string;
  row: WBTableRowWithRunSetInfo;
  truncationType: TruncationType;
  /**
   * Custom name to show when grouped by sweep
   */
  customSweepName: string | null | undefined;
  /**
   * A unique row identifier used for highlighting the row and any connected
   * charts on hover.
   */
  uniqueId: string;
};

/**
 * A component that can optionally allow the row header label to be edited. The
 * interface for this component is needlessly complex, but factoring it is out-
 * of-scope at the time of writing.
 * @param props {@link RowHeaderLabelProps}
 */
export function RowHeaderLabel({
  customRunName,
  customSweepName,
  disableRunLink,
  entityName,
  groupedBy,
  isGroup,
  isHoveringRow,
  isRunGroup,
  isSingleMode,
  onSelectRunName,
  projectName,
  row,
  truncationType,
  uniqueId,
}: RowHeaderLabelProps) {
  const isReport = useIsInLoadedReportContext();

  const runGroupText = String(groupedBy ? Run.getValue(row, groupedBy) : '');
  let groupedText = isEqual(groupedBy, RunTypes.GROUP_BY_ALL_KEY)
    ? Run.keyDisplayName(groupedBy)
    : `${groupedBy ? Run.keyDisplayName(groupedBy) + ': ' : ''}${
        groupedBy ? runGroupText : ''
      }`;
  if (
    customSweepName &&
    (groupedBy?.name === 'sweep' || groupedBy?.name === 'sweepName')
  ) {
    groupedText = Run.keyDisplayName(groupedBy) + ': ' + customSweepName;
  }

  let text = row.displayName;
  if (isGroup) {
    text = groupedText;
  } else if (customRunName) {
    text = customRunName;
  }

  const runsetProject = {entityName, name: projectName};
  const link = useLinkPath({
    disableRunLink,
    isGroup,
    isRunGroup,
    isSingleMode,
    rowName: isGroup ? row.group : row.name,
    rowProject: row.project,
    runsetProject,
  });

  const isCrossProject = isCrossProjectRun(row.project, runsetProject);
  const isInfoTooltipShown = customRunName != null || isCrossProject;

  const handleTooltipClick = useCallback(() => {
    if (!link) {
      onSelectRunName(row.name);
    }
  }, [link, onSelectRunName, row.name]);

  const textCls = clsx('text-left', {italic: customRunName});
  const badgeCls = clsx('shrink-0 opacity-0 transition-opacity', {
    'opacity-100': isHoveringRow,
  });

  return (
    <div className="flex grow shrink overflow-hidden items-center gap-3 run-row-name">
      <div className="flex-auto overflow-hidden flex gap-8">
        <TruncateTextTooltip
          aria-describedby="run-info-tooltip"
          className={textCls}
          fullText={text}
          linkPath={link}
          linkTarget={isCrossProject ? '_blank' : undefined}
          noLink={!link}
          onClick={handleTooltipClick}
          truncationType={truncationType}
        />
      </div>
      {isInfoTooltipShown ? (
        <span className={badgeCls}>
          <InfoTooltip
            id="run-info-tooltip"
            aria-label="More information about this run">
            <TailwindContents>
              <div className="flex flex-col gap-8">
                {isCrossProject && row.project != null && (
                  <div>
                    This run belongs to{' '}
                    <strong>
                      {row.project.entityName}/{row.project.name}
                    </strong>
                    .
                  </div>
                )}
                {customRunName != null && (
                  <div>
                    <strong>{customRunName}</strong> is a custom display name
                    for <strong>{row.displayName}</strong> in this{' '}
                    {isReport ? 'panel grid' : 'workspace'} only.
                  </div>
                )}
              </div>
            </TailwindContents>
          </InfoTooltip>
        </span>
      ) : null}
    </div>
  );
}
